import {
  Button,
  Input,
  MessageBar,
  MessageBarBody,
  Popover,
  PopoverSurface,
  PopoverTrigger,
  Radio,
  RadioGroup,
  Spinner,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components';
import { Pin12Filled } from '@fluentui/react-icons';
import { useState, type ReactNode } from 'react';
import type { Device } from '../../api/types';
import { useAuth } from '../../auth/AuthProvider';
import { errorText } from '../../lib/useRelay';
import { vt } from '../../theme';
import { addressHint, type AddressHint } from './model';

const useStyles = makeStyles({
  trigger: {
    display: 'inline-flex',
    alignItems: 'center',
    columnGap: '8px',
    padding: '2px 6px',
    marginInlineStart: '-6px',
    border: 'none',
    borderRadius: '6px',
    background: 'none',
    cursor: 'pointer',
    color: 'inherit',
    font: 'inherit',
    ':hover': { backgroundColor: vt.tintNeutral },
  },
  surface: { width: '324px', padding: 0, borderRadius: '10px' },
  head: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    padding: '14px 18px 12px',
    borderBottom: `1px solid ${vt.surface2}`,
  },
  title: { fontSize: '13px', fontWeight: tokens.fontWeightSemibold, color: vt.fg1 },
  mono: { fontFamily: tokens.fontFamilyMonospace, fontSize: '12px', color: vt.fg3 },
  body: { display: 'flex', flexDirection: 'column', rowGap: '4px', padding: '10px 10px 12px' },
  note: { display: 'block', fontSize: '10.5px', color: vt.fg3 },
  ipRow: { display: 'flex', alignItems: 'center', columnGap: '10px', paddingInlineStart: '30px' },
  ip: { width: '150px', fontFamily: tokens.fontFamilyMonospace },
  hint: { fontSize: '11px' },
  free: { color: vt.green },
  current: { color: vt.fg3 },
  taken: { color: vt.red },
  outside: { color: vt.orange },
  indent: { paddingInlineStart: '30px' },
  foot: {
    display: 'flex',
    justifyContent: 'flex-end',
    columnGap: '8px',
    padding: '10px 18px 12px',
    borderTop: `1px solid ${vt.surface2}`,
  },
});

const HINT_CLASS: Record<AddressHint, 'free' | 'current' | 'taken' | 'outside'> = {
  free: 'free',
  current: 'current',
  'in use': 'taken',
  'outside pool': 'outside',
};

interface Props {
  device: Device;
  devices: Device[];
  /** The device's network /24; null for a legacy device in no network, which cannot be pinned. */
  cidr: string | null;
  onSaved: () => Promise<void>;
  /** The visible address: the popover's trigger. */
  children: ReactNode;
}

/** Mockup 03's flyout under the Address cell: switch a device between an automatic and a reserved address. */
export function AddressFlyout({ device, devices, cidr, onSaved, children }: Props) {
  const s = useStyles();
  const { api } = useAuth();
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState(device.addressMode);
  const [ip, setIp] = useState(device.overlayIp);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setMode(device.addressMode);
    setIp(device.overlayIp);
    setError(null);
  };

  const hint = addressHint(ip, device, devices, cidr);
  const unchanged = mode === device.addressMode && (mode === 'auto' || ip.trim() === device.overlayIp);
  const canSave = !saving && !unchanged && (mode === 'auto' || hint === 'free' || hint === 'current');

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      if (mode === 'reserved') await api.setDeviceAddress(device.id, ip.trim());
      else await api.releaseDeviceAddress(device.id);
      await onSaved();
      setOpen(false);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Popover
      open={open}
      onOpenChange={(_e, data) => {
        if (saving) return;
        if (data.open) reset();
        setOpen(data.open);
      }}
      positioning="below-start"
      trapFocus
    >
      <PopoverTrigger disableButtonEnhancement>
        <button type="button" className={s.trigger} aria-label={`Address of ${device.name}`}>
          {children}
        </button>
      </PopoverTrigger>
      <PopoverSurface className={s.surface} aria-label={`Address of ${device.name}`}>
        <div className={s.head}>
          <span className={s.title}>Address</span>
          <span className={s.mono}>{device.name}</span>
        </div>
        <div className={s.body}>
          <RadioGroup value={mode} onChange={(_e, data) => setMode(data.value as Device['addressMode'])}>
            <Radio value="auto" label={{ children: <>Automatic<span className={s.note}>lowest free address in the pool</span></> }} />
            <Radio
              value="reserved"
              disabled={!cidr}
              label={{
                children: (
                  <>
                    Reserved
                    {!cidr && <span className={s.note}>Legacy device in no network: re-enrol it first.</span>}
                  </>
                ),
              }}
            />
          </RadioGroup>
          {mode === 'reserved' && (
            <>
              <div className={s.ipRow}>
                <Input
                  className={s.ip}
                  size="small"
                  value={ip}
                  aria-label="Reserved address"
                  onChange={(_e, data) => setIp(data.value)}
                />
                <span className={mergeClasses(s.hint, s[HINT_CLASS[hint]])} data-testid="address-hint">
                  {hint}
                </span>
              </div>
              <span className={mergeClasses(s.note, s.indent)}>
                {cidr ? `In ${cidr}. ` : ''}Survives re-enrolment, so rules can name it.
              </span>
            </>
          )}
          {error && (
            <MessageBar intent="error">
              <MessageBarBody>{error}</MessageBarBody>
            </MessageBar>
          )}
        </div>
        <div className={s.foot}>
          <Button
            appearance="primary"
            shape="circular"
            size="small"
            disabled={!canSave}
            icon={saving ? <Spinner size="tiny" /> : undefined}
            onClick={() => void save()}
          >
            Save
          </Button>
          <Button appearance="subtle" shape="circular" size="small" disabled={saving} onClick={() => setOpen(false)}>
            Cancel
          </Button>
        </div>
      </PopoverSurface>
    </Popover>
  );
}

/** The pin that marks a reserved address. */
export function PinMark({ className }: { className?: string }) {
  return (
    <Pin12Filled
      className={className}
      style={{ color: vt.brand }}
      role="img"
      aria-label="Reserved"
    />
  );
}
